import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getFactsSchemaAdmin,
  putFactsSchema,
  type FactsField,
  type FactsFieldType,
} from "@/lib/api";
import {
  Button,
  Card,
  ErrorBanner,
  Field,
  PageContainer,
  Select,
  TextInput,
  toErrorMessage,
} from "@/components/Forms";
import { ArrowLeftIcon, PlusIcon } from "@/components/Icons";

const FIELD_TYPES: FactsFieldType[] = ["TEXT", "TEXTAREA", "SELECT", "BOOLEAN"];

/**
 * Super-admin editor for the per-vertical facts schema. Lists current
 * fields, lets the operator add / remove / reorder them, then PUTs
 * the full list back. The backend diff-applies — inserts new, updates
 * existing, deletes missing — so this form treats the schema as a
 * single document.
 */
export default function FactsSchemaEditorPage() {
  const { vertical = "" } = useParams<{ vertical: string }>();
  const { t } = useTranslation();
  const [fields, setFields] = useState<FactsField[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    if (!vertical) return;
    let cancelled = false;
    setError(null);
    getFactsSchemaAdmin(vertical)
      .then((r) => !cancelled && setFields(sorted(r.fields)))
      .catch((e) => !cancelled && setError(toErrorMessage(e, t("factsSchema.loadError"))));
    return () => {
      cancelled = true;
    };
  }, [vertical, t]);

  function patch(index: number, partial: Partial<FactsField>) {
    setFields((rows) => (rows ? rows.map((r, i) => (i === index ? { ...r, ...partial } : r)) : rows));
  }

  function remove(index: number) {
    setFields((rows) => (rows ? rows.filter((_, i) => i !== index) : rows));
  }

  function add() {
    const next: FactsField = {
      key: "",
      labelSv: "",
      labelEn: "",
      type: "TEXT",
      required: false,
      order: (fields?.length ?? 0) * 10 + 10,
      placeholder: null,
      selectOptions: null,
    };
    setFields((rows) => (rows ? [...rows, next] : [next]));
  }

  async function save() {
    if (!fields) return;
    setBusy(true);
    setError(null);
    try {
      // Validate keys + labels client-side so the server isn't the only
      // gatekeeper. Empty key or duplicates would be rejected anyway.
      const keys = new Set<string>();
      for (const row of fields) {
        if (!row.key.trim() || !row.labelSv.trim() || !row.labelEn.trim()) {
          setError(t("factsSchema.errorRowIncomplete"));
          setBusy(false);
          return;
        }
        if (keys.has(row.key.trim())) {
          setError(t("factsSchema.errorDuplicateKey", { key: row.key.trim() }));
          setBusy(false);
          return;
        }
        keys.add(row.key.trim());
      }
      const out = await putFactsSchema(vertical, fields);
      setFields(sorted(out.fields));
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setError(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageContainer
      title={t("factsSchema.title", { vertical })}
      subtitle={t("factsSchema.subtitle")}
      actions={
        <Link
          to="/prompt-modules"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeftIcon size={14} /> {t("promptModules.backToList")}
        </Link>
      }
    >
      <ErrorBanner message={error} />

      {fields === null ? (
        <p className="text-sm text-muted-foreground">{t("forms.loading")}</p>
      ) : (
        <Card title={t("factsSchema.fieldsTitle")}>
          <div className="space-y-3">
            {fields.length === 0 && (
              <p className="text-sm text-muted-foreground">{t("factsSchema.empty")}</p>
            )}
            {fields.map((f, i) => (
              <FieldRow
                key={`${f.key}-${i}`}
                index={i}
                field={f}
                onChange={(p) => patch(i, p)}
                onRemove={() => remove(i)}
              />
            ))}
            <div className="flex items-center justify-between pt-2">
              <Button onClick={add}>
                <PlusIcon size={12} /> {t("factsSchema.addField")}
              </Button>
              <div className="flex items-center gap-3">
                {savedAt && (
                  <span className="text-[11px] text-muted-foreground">
                    {t("factsSchema.savedAt", { time: savedAt })}
                  </span>
                )}
                <Button variant="primary" onClick={save} disabled={busy}>
                  {busy ? t("forms.saving") : t("forms.save")}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}
    </PageContainer>
  );
}

interface RowProps {
  index: number;
  field: FactsField;
  onChange: (partial: Partial<FactsField>) => void;
  onRemove: () => void;
}

function FieldRow({ field, onChange, onRemove }: RowProps) {
  const { t } = useTranslation();
  return (
    <div className="rounded-md border border-border bg-muted/20 p-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label={t("factsSchema.colKey")}>
          <TextInput
            value={field.key}
            onChange={(e) => onChange({ key: e.target.value })}
            placeholder="address"
          />
        </Field>
        <Field label={t("factsSchema.colType")}>
          <Select value={field.type} onChange={(e) => onChange({ type: e.target.value as FactsFieldType })}>
            {FIELD_TYPES.map((tp) => (
              <option key={tp} value={tp}>
                {tp}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("factsSchema.colOrder")}>
          <TextInput
            type="number"
            value={String(field.order)}
            onChange={(e) => onChange({ order: Number(e.target.value) || 0 })}
          />
        </Field>
        <Field label={t("factsSchema.colLabelSv")}>
          <TextInput
            value={field.labelSv}
            onChange={(e) => onChange({ labelSv: e.target.value })}
          />
        </Field>
        <Field label={t("factsSchema.colLabelEn")}>
          <TextInput
            value={field.labelEn}
            onChange={(e) => onChange({ labelEn: e.target.value })}
          />
        </Field>
        <Field label={t("factsSchema.colPlaceholder")}>
          <TextInput
            value={field.placeholder ?? ""}
            onChange={(e) => onChange({ placeholder: e.target.value || null })}
          />
        </Field>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={field.required}
            onChange={(e) => onChange({ required: e.target.checked })}
          />
          {t("factsSchema.colRequired")}
        </label>
        <Button variant="destructive" onClick={onRemove}>
          {t("factsSchema.removeField")}
        </Button>
      </div>
    </div>
  );
}

function sorted(rows: FactsField[]): FactsField[] {
  return [...rows].sort((a, b) => a.order - b.order || a.key.localeCompare(b.key));
}
